// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { QRScannerV2 } from '@/components/staff/QRScannerV2';
import { Shield, Info } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { parseLocalDate } from '@/utils/formatters';

const DEMO_EVENT_ID = 'f83c2769-b4aa-4ba9-9e3e-5e205fd40047';

interface Event {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
}

const DemoCheckIn: React.FC = () => {
  const [event, setEvent] = useState<Event | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchDemoEvent = async () => {
      try {
        const { data, error } = await supabase
          .from('events')
          .select('id, name, start_date, end_date')
          .eq('id', DEMO_EVENT_ID)
          .single();

        if (error) throw error;
        setEvent(data);
      } catch (error) {
        console.error('Error fetching demo event:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDemoEvent();
  }, []);

  if (isLoading) {
    return (
      <div className="text-center text-muted-foreground py-8">Loading demo event...</div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Check-In Demo</h1>
        <p className="text-gray-600">Scan QR codes and check in teams/spectators</p>
      </div>

      {/* Demo Event Info */}
      {event && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Info className="h-5 w-5 text-blue-600" />
              <div>
                <p className="font-medium text-blue-900">Demo Event: {event.name}</p>
                <p className="text-sm text-blue-700">
                  {format(parseLocalDate(event.start_date), 'MMM d, yyyy')} - {format(parseLocalDate(event.end_date), 'MMM d, yyyy')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Staff Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Staff Instructions
          </CardTitle>
          <CardDescription>How to use the check-in scanner</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold text-xs">
              1
            </div>
            <div>
              <p className="font-medium">Choose scanning mode</p>
              <p className="text-muted-foreground">Use Camera mode for physical QR codes or Manual mode to type codes</p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold text-xs">
              2
            </div>
            <div>
              <p className="font-medium">Scan or enter QR code</p>
              <p className="text-muted-foreground">Point camera at attendee's QR code or manually type the code value</p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold text-xs">
              3
            </div>
            <div>
              <p className="font-medium">Verify result</p>
              <p className="text-muted-foreground">Green checkmark = success, Red X = already used or invalid</p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold text-xs">
              4
            </div>
            <div>
              <p className="font-medium">Scan next</p>
              <p className="text-muted-foreground">Click "Scan Next" button to process another attendee</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* QR Scanner */}
      <QRScannerV2 />
    </div>
  );
};

export default DemoCheckIn;